import { useState, type FormEvent } from "react";
import type { UserCredential } from "firebase/auth";
import { useSession } from "../contexts/SessionContext";

export function LoginPage() {
  const { signIn, status, error } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const isLoading = status === "loadingProfile";
  const displayedError = localError ?? error;

  async function exchangeFirebaseUser(cred: UserCredential) {
    const idToken = await cred.user.getIdToken();
    await signIn(idToken);
  }

  async function handleEmailSubmit(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);
    try {
      const { signInEmail } = await import("../lib/firebase-auth");
      const cred = await signInEmail(email, password);
      await exchangeFirebaseUser(cred);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : "Sign-in failed");
    }
  }

  async function handleGoogleSignIn() {
    setLocalError(null);
    try {
      const { signInGoogle } = await import("../lib/firebase-auth");
      const cred = await signInGoogle();
      await exchangeFirebaseUser(cred);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : "Sign-in failed");
    }
  }

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#0f172a",
    }}>
      <div style={{
        width: "100%",
        maxWidth: "400px",
        padding: "2rem",
        borderRadius: "12px",
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.1)",
      }}>
        <h1 style={{ textAlign: "center", color: "#fff", marginBottom: "0.5rem" }}>
          WonderCare Analytics
        </h1>
        <p style={{ textAlign: "center", color: "rgba(255,255,255,0.5)", marginBottom: "1.5rem", fontSize: "0.875rem" }}>
          Sign in to access analytics dashboards
        </p>

        {displayedError && (
          <div style={{
            marginBottom: "1rem",
            padding: "0.75rem",
            borderRadius: "8px",
            background: "rgba(239,68,68,0.1)",
            border: "1px solid rgba(239,68,68,0.2)",
            color: "#f87171",
            fontSize: "0.875rem",
          }}>
            {displayedError}
          </div>
        )}

        <form onSubmit={handleEmailSubmit}>
          <div style={{ marginBottom: "1rem" }}>
            <label style={{ display: "block", color: "rgba(255,255,255,0.7)", marginBottom: "0.25rem", fontSize: "0.875rem" }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{
                width: "100%",
                padding: "0.625rem 0.75rem",
                borderRadius: "8px",
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#fff",
                fontSize: "0.875rem",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div style={{ marginBottom: "1.5rem" }}>
            <label style={{ display: "block", color: "rgba(255,255,255,0.7)", marginBottom: "0.25rem", fontSize: "0.875rem" }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{
                width: "100%",
                padding: "0.625rem 0.75rem",
                borderRadius: "8px",
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#fff",
                fontSize: "0.875rem",
                boxSizing: "border-box",
              }}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: "100%",
              padding: "0.625rem",
              borderRadius: "8px",
              background: "#3b82f6",
              color: "#fff",
              fontWeight: "500",
              border: "none",
              cursor: isLoading ? "not-allowed" : "pointer",
              opacity: isLoading ? 0.5 : 1,
              fontSize: "0.875rem",
            }}
          >
            {isLoading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          margin: "1.25rem 0",
          color: "rgba(255,255,255,0.4)",
          fontSize: "0.75rem",
        }}>
          <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.1)" }} />
          <span>or</span>
          <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.1)" }} />
        </div>

        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={isLoading}
          style={{
            width: "100%",
            padding: "0.625rem",
            borderRadius: "8px",
            background: "rgba(255,255,255,0.05)",
            color: "#fff",
            fontWeight: "500",
            border: "1px solid rgba(255,255,255,0.1)",
            cursor: isLoading ? "not-allowed" : "pointer",
            opacity: isLoading ? 0.5 : 1,
            fontSize: "0.875rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
          }}
        >
          <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.1 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.3 2.4-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z" />
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4 5.6l6.2 5.2c-.4.4 6.5-4.7 6.5-14.8 0-1.3-.1-2.4-.4-3.5z" />
          </svg>
          Sign in with Google
        </button>
      </div>
    </div>
  );
}
